import React, { useEffect, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { formatCardNumber, formatExpiry, formatCVV, validateCardNumber, validateExpiry, validateCVV, validateCardholderName, detectCardType } from '../utils/paymentValidation';
interface PaymentInputProps {
  onValidationChange: (isValid: boolean) => void;
  onCardDataChange: (data: {
    cardNumber: string;
    expiry: string;
    cvv: string;
    cardholderName: string;
  }) => void;
}
// Card type icons/badges
const CardTypeBadge = ({
  cardType
}: {
  cardType: string;
}) => {
  const getBadgeColor = () => {
    switch (cardType) {
      case 'Visa':
        return 'bg-blue-500';
      case 'Mastercard':
        return 'bg-orange-500';
      case 'American Express':
        return 'bg-sky-500';
      case 'Discover':
        return 'bg-orange-600';
      case 'JCB':
        return 'bg-red-500';
      case 'Diners Club':
        return 'bg-blue-600';
      case 'UnionPay':
        return 'bg-red-600';
      default:
        return 'bg-moss-gray';
    }
  };
  if (cardType === 'Unknown') return null;
  return <div className={`${getBadgeColor()} text-white text-xs font-bold px-2 py-1 rounded`} data-id="element-397">
      {cardType}
    </div>;
};
export function PaymentInput({
  onValidationChange,
  onCardDataChange
}: PaymentInputProps) {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [cardNumberError, setCardNumberError] = useState('');
  const [expiryError, setExpiryError] = useState('');
  const [cvvError, setCvvError] = useState('');
  const [nameError, setNameError] = useState('');
  const [cardNumberTouched, setCardNumberTouched] = useState(false);
  const [expiryTouched, setExpiryTouched] = useState(false);
  const [cvvTouched, setCvvTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const cardType = detectCardType(cardNumber);
  // Validate and update parent component
  useEffect(() => {
    const cardValidation = validateCardNumber(cardNumber);
    const expiryValidation = validateExpiry(expiry);
    const cvvValidation = validateCVV(cvv, cardType);
    const nameValidation = validateCardholderName(cardholderName);
    const isValid = cardValidation.isValid && expiryValidation.isValid && cvvValidation.isValid && nameValidation.isValid;
    onValidationChange(isValid);
    onCardDataChange({
      cardNumber,
      expiry,
      cvv,
      cardholderName
    });
  }, [cardNumber, expiry, cvv, cardholderName, cardType]);
  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value);
    setCardNumber(formatted);
    if (cardNumberTouched) {
      const validation = validateCardNumber(formatted);
      setCardNumberError(validation.error || '');
    }
  };
  const handleExpiryChange = (value: string) => {
    const formatted = formatExpiry(value);
    setExpiry(formatted);
    if (expiryTouched) {
      const validation = validateExpiry(formatted);
      setExpiryError(validation.error || '');
    }
  };
  const handleCVVChange = (value: string) => {
    const formatted = formatCVV(value, cardType);
    setCvv(formatted);
    if (cvvTouched) {
      const validation = validateCVV(formatted, cardType);
      setCvvError(validation.error || '');
    }
  };
  const handleNameChange = (value: string) => {
    setCardholderName(value);
    if (nameTouched) {
      const validation = validateCardholderName(value);
      setNameError(validation.error || '');
    }
  };
  const handleCardNumberBlur = () => {
    setCardNumberTouched(true);
    const validation = validateCardNumber(cardNumber);
    setCardNumberError(validation.error || '');
  };
  const handleExpiryBlur = () => {
    setExpiryTouched(true);
    const validation = validateExpiry(expiry);
    setExpiryError(validation.error || '');
  };
  const handleCVVBlur = () => {
    setCvvTouched(true);
    const validation = validateCVV(cvv, cardType);
    setCvvError(validation.error || '');
  };
  const handleNameBlur = () => {
    setNameTouched(true);
    const validation = validateCardholderName(cardholderName);
    setNameError(validation.error || '');
  };
  return <div className="space-y-4" data-id="element-398">
      {/* Card Number */}
      <div data-id="element-399">
        <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-400">
          Card Number
        </label>
        <div className="relative" data-id="element-401">
          <input type="text" value={cardNumber} onChange={e => handleCardNumberChange(e.target.value)} onBlur={handleCardNumberBlur} placeholder="1234 5678 9012 3456" inputMode="numeric" className={`w-full px-4 py-3 pr-24 bg-white border-2 rounded-ios text-charcoal placeholder-moss-gray focus:outline-none transition-all ${cardNumberError && cardNumberTouched ? 'border-red-500 focus:border-red-500' : 'border-sand focus:border-dark-green'}`} data-id="element-402" />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2" data-id="element-403">
            <CardTypeBadge cardType={cardType} data-id="element-404" />
            <CreditCard size={20} className="text-moss-gray" data-id="element-405" />
          </div>
        </div>
        {cardNumberError && cardNumberTouched && <p className="mt-1 text-sm text-red-500" data-id="element-406">{cardNumberError}</p>}
        {!cardNumberError && cardType !== 'Unknown' && cardNumber.length > 0 && <p className="mt-1 text-sm text-dark-green" data-id="element-407">
              ✓ {cardType} detected
            </p>}
      </div>

      {/* Expiry and CVV */}
      <div className="grid grid-cols-2 gap-4" data-id="element-408">
        <div data-id="element-409">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-410">
            Expiry
          </label>
          <input type="text" value={expiry} onChange={e => handleExpiryChange(e.target.value)} onBlur={handleExpiryBlur} placeholder="MM/YY" inputMode="numeric" className={`w-full px-4 py-3 bg-white border-2 rounded-ios text-charcoal placeholder-moss-gray focus:outline-none transition-all ${expiryError && expiryTouched ? 'border-red-500 focus:border-red-500' : 'border-sand focus:border-dark-green'}`} data-id="element-411" />
          {expiryError && expiryTouched && <p className="mt-1 text-sm text-red-500" data-id="element-412">{expiryError}</p>}
        </div>

        <div data-id="element-413">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-414">
            CVV {cardType === 'American Express' && '(4 digits)'}
          </label>
          <input type="text" value={cvv} onChange={e => handleCVVChange(e.target.value)} onBlur={handleCVVBlur} placeholder={cardType === 'American Express' ? '1234' : '123'} inputMode="numeric" className={`w-full px-4 py-3 bg-white border-2 rounded-ios text-charcoal placeholder-moss-gray focus:outline-none transition-all ${cvvError && cvvTouched ? 'border-red-500 focus:border-red-500' : 'border-sand focus:border-dark-green'}`} data-id="element-415" />
          {cvvError && cvvTouched && <p className="mt-1 text-sm text-red-500" data-id="element-416">{cvvError}</p>}
        </div>
      </div>

      {/* Cardholder Name */}
      <div data-id="element-417">
        <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-418">
          Cardholder Name
        </label>
        <input type="text" value={cardholderName} onChange={e => handleNameChange(e.target.value)} onBlur={handleNameBlur} placeholder="John Doe" className={`w-full px-4 py-3 bg-white border-2 rounded-ios text-charcoal placeholder-moss-gray focus:outline-none transition-all ${nameError && nameTouched ? 'border-red-500 focus:border-red-500' : 'border-sand focus:border-dark-green'}`} data-id="element-419" />
        {nameError && nameTouched && <p className="mt-1 text-sm text-red-500" data-id="element-420">{nameError}</p>}
      </div>
    </div>;
}